/* eslint-disable no-console */
/* eslint-disable indent */
import React, { lazy, Suspense } from "react";
import { Switch, Route } from "react-router-dom";
import { useLocation } from "react-router";
import config from "config";

import { useMeta } from "context/metaContext";

import ErrorBoundary from "components/errorBoundary";
import { FullPageSpinner } from "components/Loader";
import { fetchPaymentLink } from "lib/requests";
import { computeRecurringMeta, generateCurrencyId } from "utils/enums";

const ProductPayment = lazy(() => import("./pages/ProductPayment"));
const OneTimePayment = lazy(() => import("./pages/OneTimePayment"));
const Donation = lazy(() => import("./pages/Donation"));
const Recurring = lazy(() => import("./pages/Recurring"));
const InvalidLink = lazy(() => import("./pages/InvalidLink"));
const InactiveLink = lazy(() => import("./pages/InactiveLink"));

const squad = window.squad;

function App() {
  const { pathname } = useLocation();
  const { metaValues, setMetaValues, setIsLoading } = useMeta();

  const [publicKey, setPublicKey] = React.useState("");
  const [linkDetails, setLinkDetails] = React.useState(null);
  const [isLinkValid, setIsLinkValid] = React.useState(true);
  const [isLinkInactive, setIsLinkInactive] = React.useState(false);

  function handleRedirect() {
    if (metaValues?.redirectLink) {
      window.location = metaValues?.redirectLink;
    } else {
      window.location = config.HOMEPAGE;
    }
    return;
  }

  function initWidget(params, publicKey) {
    params.currency_id = generateCurrencyId(params.currency_code);
    const squadInstance = new squad({
      onClose: () => {
        console.log("Widget closed");
        setIsLoading(false);
      },
      onLoad: () => {
        console.log("Widget loaded successfully");
        setIsLoading(false);
      },
      onSuccess: () => handleRedirect(),
      key: publicKey,
      ...params
    });

    squadInstance.setup();
    squadInstance.open();
  }

  React.useEffect(() => {
    if (pathname !== "/") {
      fetchPaymentLink(pathname.replace("/", ""))
        .then(res => {
          if (res.status === 200) {
            // check if link isactive
            if (res.data.data.link_details.link_status === 0) {
              setIsLinkInactive(true);
            }

            // check if link is expired
            if (
              res?.data?.data?.link_details?.expire_by &&
              new Date(res?.data?.data?.link_details?.expire_by) < new Date()
            ) {
              setIsLinkInactive(true);
            }

            // chaeck is donation start date has reached
            if (
              res?.data?.data?.link_details?.link_type === "donation" &&
              new Date(res?.data?.data?.link_details?.link_type?.meta?.start_date) < new Date()
            ) {
              setIsLinkInactive(true);
            }

            setPublicKey(res.data.data.devkit);
            setLinkDetails(res.data.data.link_details ?? "");
            const { link_details: metaData } = res.data.data;

            const { frequency, duration } = computeRecurringMeta(
              metaData?.meta?.interval,
              metaData?.meta?.billing_cycle
            );

            const recurring = {
              duration: String(duration),
              frequency: String(frequency),
              plan_code: metaData?.hash
            };

            // store the meta details for use later
            if (metaData) {
              setMetaValues({
                name: metaData?.name,
                supportEmail: metaData?.support_email,
                supportPhone: metaData?.support_phone,
                terms: metaData?.terms_condition,
                returnMessage: metaData?.return_msg,
                hash: metaData?.hash,
                returnPolicy: metaData?.return_policy,
                redirectLink: metaData?.redirect_link,
                linkType: metaData?.link_type,
                description: metaData?.description,
                pickup_location: metaData?.pickup_location,
                merchantId: metaData?.merchant_id,
                verified: res?.data?.data?.verified ?? false,
                storeLogo: res?.data?.data?.logo?.url,
                storeName: res?.data?.data?.name,
                display_name: res?.data?.data?.display_name,
                recurring
              });
            }
          } else {
            setIsLinkValid(false);
          }
        })
        .catch(() => {
          setIsLinkValid(false);
        });
    } else {
      setIsLinkValid(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ErrorBoundary>
      <Suspense fallback={<FullPageSpinner />}>
        <Switch>
          <Route exact path="/:hash">
            <RenderView
              initWidget={initWidget}
              linkDetails={linkDetails}
              isLinkValid={isLinkValid}
              publicKey={publicKey}
              isLinkInactive={isLinkInactive}
            />
          </Route>
          <Route exact path="/">
            <RenderView
              initWidget={initWidget}
              linkDetails={linkDetails}
              isLinkValid={isLinkValid}
              publicKey={publicKey}
              isLinkInactive={isLinkInactive}
            />
          </Route>
        </Switch>
      </Suspense>
    </ErrorBoundary>
  );
}

export default App;

function RenderView({ initWidget, linkDetails, isLinkValid, publicKey, isLinkInactive }) {
  return (
    <>
      {/* <ProductPayment initWidget={initWidget} publicKey={publicKey} data={linkDetails} /> */}

      {isLinkInactive ? (
        <InactiveLink />
      ) : linkDetails?.link_type === "otp" ? (
        <OneTimePayment initWidget={initWidget} publicKey={publicKey} data={linkDetails} />
      ) : linkDetails?.link_type === "recurring" ? (
        <Recurring initWidget={initWidget} publicKey={publicKey} data={linkDetails} />
      ) : linkDetails?.link_type === "donation" ? (
        <Donation initWidget={initWidget} publicKey={publicKey} data={linkDetails} />
      ) : linkDetails?.link_type === "product" ? (
        <ProductPayment initWidget={initWidget} publicKey={publicKey} data={linkDetails} />
      ) : !isLinkValid ? (
        <InvalidLink />
      ) : (
        <FullPageSpinner />
      )}
    </>
  );
}
